import { Order, OrderDealCatalogItem, OrderDealOrderModifierRow } from './order';

export function buildDealOrderSlotModifierKey(
  dealOrderId: string,
  lineIndex: number,
  menuItemId: string
): string {
  return `${dealOrderId}:${lineIndex}:${menuItemId}`;
}

export function buildDealOrderLegacyModifierKey(dealOrderId: string, menuItemId: string): string {
  return `${dealOrderId}:${menuItemId}`;
}

export function collectDealCatalogMenuItemIds(
  catalogItems: ReadonlyArray<OrderDealCatalogItem> | null | undefined
): string[] {
  return (catalogItems ?? [])
    .map((catalogItem) => catalogItem.menuItem?.id)
    .filter((menuItemId): menuItemId is string => Boolean(menuItemId));
}

export function dealTemplateHasDuplicateMenuItems(menuItemIds: readonly string[]): boolean {
  return new Set(menuItemIds).size !== menuItemIds.length;
}

export function resolveDealOrderLineIndex(
  modifierRow: Pick<OrderDealOrderModifierRow, 'line_index'>
): number {
  return modifierRow.line_index ?? 0;
}

export function resolveDealOrderModifierMenuItemId(
  modifierRow: Pick<OrderDealOrderModifierRow, 'menu_item_id' | 'menuItem'>
): string | undefined {
  return modifierRow.menu_item_id ?? modifierRow.menuItem?.id;
}

/**
 * Pre-slot-index rows are all line_index 0. Use menu_item_id-only matching only when
 * the deal template has unique item ids — duplicate slots with extras only on line 0
 * also persist solely as line_index 0 and must keep exact slot matching.
 */
export function dealOrderUsesLegacyModifierMatching(
  modifierRows: OrderDealOrderModifierRow[],
  catalogMenuItemIds: readonly string[] = []
): boolean {
  if (modifierRows.length === 0) {
    return false;
  }

  if (dealTemplateHasDuplicateMenuItems(catalogMenuItemIds)) {
    return false;
  }

  return modifierRows.every((modifierRow) => resolveDealOrderLineIndex(modifierRow) === 0);
}

export function buildDealOrderModifierLookupKey(
  dealOrderId: string,
  lineIndex: number,
  menuItemId: string,
  modifierRows: OrderDealOrderModifierRow[],
  catalogMenuItemIds: readonly string[] = []
): string {
  if (dealOrderUsesLegacyModifierMatching(modifierRows, catalogMenuItemIds)) {
    return buildDealOrderLegacyModifierKey(dealOrderId, menuItemId);
  }

  return buildDealOrderSlotModifierKey(dealOrderId, lineIndex, menuItemId);
}

export function buildSelectedDealModifiersByDealSlot(
  orders: Order[]
): Map<string, OrderDealOrderModifierRow[]> {
  const modifiersBySlot = new Map<string, OrderDealOrderModifierRow[]>();

  for (const order of orders) {
    for (const dealOrder of order.deals ?? []) {
      const modifierRows = dealOrder.modifiers ?? [];
      const catalogMenuItemIds = collectDealCatalogMenuItemIds(dealOrder.deal?.items);
      const useLegacyMatching = dealOrderUsesLegacyModifierMatching(
        modifierRows,
        catalogMenuItemIds
      );

      for (const modifierRow of modifierRows) {
        const menuItemId = resolveDealOrderModifierMenuItemId(modifierRow);
        if (!menuItemId) {
          continue;
        }

        const slotKey = useLegacyMatching
          ? buildDealOrderLegacyModifierKey(dealOrder.id, menuItemId)
          : buildDealOrderSlotModifierKey(
              dealOrder.id,
              resolveDealOrderLineIndex(modifierRow),
              menuItemId
            );

        const slotModifiers = modifiersBySlot.get(slotKey);
        if (slotModifiers) {
          slotModifiers.push(modifierRow);
        } else {
          modifiersBySlot.set(slotKey, [modifierRow]);
        }
      }
    }
  }

  return modifiersBySlot;
}
